import { skipToken, useQuery } from '@tanstack/react-query'
import { memo, type ReactNode, useEffect, useMemo } from 'react'

import { ensureError, TechnicalError } from '@orus.eu/error'
import { isPharaohTheme, usePharaohTheme, useSetCoBranding } from '@orus.eu/pharaoh'

import { useTRPC } from '../../client.ts'
import { coBrandingFactories, EmbeddingPartnerProvider } from '../../lib/embedding-partner.ts'
import { useEmbeddingPartnerSelection } from '../../lib/hooks/use-embedding-partner-selection.ts'
import { logger } from '../../lib/logger.ts'
import { useSession } from '../../lib/session.ts'

export const EmbeddingPartnerWrapper = memo<{ children: ReactNode }>(function EmbeddingPartnerWrapper({ children }) {
  const trpc = useTRPC()

  const session = useSession()

  const setCoBranding = useSetCoBranding()
  const { setTheme } = usePharaohTheme()

  const hasPermissions =
    session?.permissions.type === 'client' &&
    session.permissions.rolesPermissions.flatMap((r) => r.permissions).includes('contract.read')

  const { data: contracts } = useQuery(
    trpc.contracts.listMyContracts.queryOptions(hasPermissions ? undefined : skipToken),
  )

  const { embeddingPartnerId, setEmbeddingPartnerId, forceOrusDefault } = useEmbeddingPartnerSelection(contracts)

  // This effect reacts to embeddingPartner changes to update the co-branding accordingly
  useEffect(() => {
    if (!embeddingPartnerId) {
      setCoBranding(null)
      setTheme('light')
      return
    }

    const coBrandingFactory = coBrandingFactories[embeddingPartnerId]
    if (!coBrandingFactory) {
      // we have no co-branding registered for it, so we fall back to an unbranded Orus funnel rather than crashing.
      // (the partner stays associated with the subscription, organization is preserved)
      logger.error(
        new TechnicalError('No co-branding registered for embedding partner', {
          context: { embeddingPartnerId },
        }),
      )
      setCoBranding(null)
      setTheme('light')
      return
    }

    if (isPharaohTheme(embeddingPartnerId)) setTheme(embeddingPartnerId)

    let cancelled = false

    coBrandingFactory()
      .then((coBranding) => {
        if (cancelled) {
          return
        }

        setCoBranding(coBranding)
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return
        }

        logger.warning('Error while loading co-branding', {
          errStack: ensureError(err).stack ?? null,
        })
      })

    return () => {
      cancelled = true
    }
  }, [embeddingPartnerId, setCoBranding, setTheme])

  const embeddingPartnerContextValue = useMemo(
    () => ({ embeddingPartnerId, setEmbeddingPartnerId, forceOrusDefault }),
    [embeddingPartnerId, setEmbeddingPartnerId, forceOrusDefault],
  )

  return <EmbeddingPartnerProvider value={embeddingPartnerContextValue}>{children}</EmbeddingPartnerProvider>
})
